import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DNIUploader } from "../DniUploader";
import { LocationSelectors } from "../LocationSelectors";
import { SelfieUploader } from "../SelfieUploader";
import { ManagerInfo } from "../../services/dto/request/branches-request-dto";


const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
};

interface ManagerFieldsProps {
  disabled: boolean;
  errors: Record<string, string>;
  handleManagerInfoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  managerInfo: ManagerInfo;
  existingFiles?: {
    managerDniFront: string | null;
    managerDniBack: string | null;
    managerSelfie: string | null;
  };
}

export function ManagerFields({ disabled, errors, handleManagerInfoChange, managerInfo, existingFiles }: ManagerFieldsProps) {
  const { t } = useTranslation();

  const handleFileChange = (fieldName: string, file: File | null) => {
    const event = {
      target: {
        name: fieldName,
        value: file
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleManagerInfoChange(event);
    
    const missingDocs: string[] = [];
    if (!(fieldName === 'managerDniFront' ? file : managerInfo.managerDniFront)) {
      missingDocs.push("DNI frente");
    }
    if (!(fieldName === 'managerDniBack' ? file : managerInfo.managerDniBack)) {
      missingDocs.push("DNI dorso");
    }
    if (!(fieldName === 'managerSelfie' ? file : managerInfo.managerSelfie)) {
      missingDocs.push("selfie");
    }

    const updateErrorMessage = {
      target: {
        name: 'dniDocuments',
        value: missingDocs.length > 0 ? `Falta cargar: ${missingDocs.join(", ")}` : ''
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleManagerInfoChange(updateErrorMessage);
  };

  return (
    <div className="space-y-4">
      <TextField
        type="text"
        name="managerName"
        onChange={handleManagerInfoChange}
        value={managerInfo.managerName}
        error={!!errors.managerName}
        helperText={errors.managerName}
        label={t("branches.create.manager.name")}
        variant="standard"
        disabled={disabled}
        fullWidth
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="managerLastName"
        onChange={handleManagerInfoChange}
        value={managerInfo.managerLastName}
        error={!!errors.managerLastName}
        helperText={errors.managerLastName}
        label={t("branches.create.manager.lastName")}
        variant="standard"
        disabled={disabled}
        fullWidth
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="managerPhone"
        onChange={handleManagerInfoChange}
        value={managerInfo.managerPhone}
        error={!!errors.managerPhone}
        helperText={errors.managerPhone}
        label={t("branches.create.manager.phone")}
        variant="standard"
        disabled={disabled}
        fullWidth
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;
          value = value.replace(/[^\d]/g, "");
          if (value.length > 10) {
            value = value.substring(0, 10);
          }
          input.value = value;
          handleManagerInfoChange({
            target: { name: "managerPhone", value: value }
          } as unknown as React.ChangeEvent<HTMLInputElement>);
        }}
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="managerAddress"
        onChange={handleManagerInfoChange}
        value={managerInfo.managerAddress}
        error={!!errors.managerAddress}
        helperText={errors.managerAddress}
        label={t("branches.create.manager.address")}
        variant="standard"
        disabled={disabled}
        fullWidth
        sx={textFieldStyles}
      />

      <div className="flex text-black  items-end gap-4">
        <div className="flex flex-col gap-1 flex-1">
          <TextField
            id="managerDocumentNumber"
            name="managerDocumentNumber"
            label="CUIL"
            disabled={disabled}
            error={!!errors.managerDocumentNumber}
            helperText={errors.managerDocumentNumber}
            value={managerInfo.managerDocumentNumber}
            onChange={handleManagerInfoChange}
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              let value = input.value;
              value = value.replace(/[^\d]/g, "");
              input.value = value;
            }}
            type="number"
            variant="standard"
            sx={textFieldStyles}
          />
        </div>
      </div>

      <LocationSelectors
        key="manager-location"
        initialDepartmentId={managerInfo.managerProvinceId > 0 ? managerInfo.managerProvinceId.toString() : ""}
        initialCityId={managerInfo.managerCityId > 0 ? managerInfo.managerCityId.toString() : ""}
        disabled={disabled}
        onProvinceChange={(provinceId) => {
          handleManagerInfoChange({
            target: { name: "managerProvinceId", value: parseInt(provinceId) },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
        }}
        onCityChange={(cityId) => {
          handleManagerInfoChange({
            target: { name: "managerCityId", value: parseInt(cityId) },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
        }}
      />
      {(errors.managerProvinceId || errors.managerCityId) && (
        <p className="text-red mt-2 text-xs">
          {errors.managerProvinceId || errors.managerCityId}
        </p>
      )}

      <div className="text-center mt-4 text-black">
        <div className="flex flex-col gap-4 border rounded-lg p-4">
          <DNIUploader
            onFilesChange={(front, back) => {
              handleFileChange('managerDniFront', front);
              handleFileChange('managerDniBack', back);
            }}
          />
          <SelfieUploader
            fileName={managerInfo.managerName}
            onFileChange={(file) => {
              handleFileChange('managerSelfie', file);
            }}
          />          
        </div>
        
        {/* Mostrar archivos existentes */}
        {existingFiles && (existingFiles.managerDniFront || existingFiles.managerDniBack || existingFiles.managerSelfie) && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 font-medium mb-2">
              {t("branches.edit.existing-files")}:
            </p>
            <div className="flex flex-wrap gap-2">
              {existingFiles.managerDniFront && (
                <a 
                  href={existingFiles.managerDniFront} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                >
                  DNI Frente
                </a>
              )}
              {existingFiles.managerDniBack && (
                <a 
                  href={existingFiles.managerDniBack} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                >
                  DNI Dorso
                </a>
              )}
              {existingFiles.managerSelfie && (
                <a 
                  href={existingFiles.managerSelfie} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                >
                  Selfie
                </a>
              )}
            </div>
          </div>
        )}
        {errors.dniDocuments && (
          <p className="text-red mt-2 text-xs">{errors.dniDocuments}</p>
        )}
      </div>
    </div>
  );
}